// BookingsPage.js
import React, { useState, useEffect } from 'react';
import {
  Typography, Box, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Button, Chip, Alert, TextField,
  MenuItem, InputLabel, FormControl, Grid, Dialog,
  DialogTitle, DialogContent, DialogActions, TablePagination,Select
} from '@mui/material';
import { SaveAlt } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import BookTable from './BookTable2';
import moment from 'moment';

const BookingsPage = () => {
  const [bookings, setBookings] = useState([]);
  const [filteredBookings, setFilteredBookings] = useState([]);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState(new Date());
  const [openBookingModal, setOpenBookingModal] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [nextStatus, setNextStatus] = useState('');

  useEffect(() => { fetchBookings(); }, []);
  useEffect(() => { applyFilters(); }, [search, statusFilter, bookings, dateFilter]);

  const fetchBookings = async () => {
    try {
      const res = await api.get('/bookings');
      setBookings(res.data);
    } catch {
      setError('Failed to fetch bookings');
    }
  };

  const applyFilters = () => {
    setPage(0);
    const filtered = bookings.filter(b => {
      const bookingDate = moment.utc(b.date).format('YYYY-MM-DD');
      const selectedDate = moment.utc(dateFilter).format('YYYY-MM-DD');
      const isSameDate = bookingDate === selectedDate;
      const matchesSearch = b.user.name?.toLowerCase().includes(search.toLowerCase()) || b.user.email?.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = statusFilter === 'all' || b.status === statusFilter;
      return isSameDate && matchesSearch && matchesStatus;
    });
    setFilteredBookings(filtered);
  };

  const handleUpdateBookingStatus = async () => {
    try {
      await api.patch(`/bookings/${selectedBookingId}/status`, { status: nextStatus });
      fetchBookings();
      setConfirmDialogOpen(false);
    } catch {
      setError('Failed to update booking status');
      setConfirmDialogOpen(false);
    }
  };

  const exportToCSV = () => {
    const csvRows = [['Customer Name','Email','Table','Date','Time','Guests','Status'],
      ...filteredBookings.map(b => [b.user.name,b.user.email,b.table.tableNumber,new Date(b.date).toLocaleDateString(),b.time,b.numberOfGuests,b.status])
    ];
    const csvContent = csvRows.map(e => e.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bookings.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => { setRowsPerPage(parseInt(event.target.value, 10)); setPage(0); };
  const paginatedBookings = filteredBookings.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box sx={{ display: 'flex' }}>
      <Sidebar />
      <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5">Manage Bookings</Typography>
          <Button variant="contained" onClick={() => { setEditingBooking(null); setOpenBookingModal(true); }}>Add Booking</Button>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Paper sx={{ p: 2, mb: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={3}>
                <DatePicker label="Filter by date" value={dateFilter} onChange={setDateFilter} renderInput={(params) => <TextField {...params} fullWidth />} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField label="Search by name or email" value={search} onChange={(e) => setSearch(e.target.value)} fullWidth />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="confirmed">Confirmed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <Button variant="outlined" fullWidth startIcon={<SaveAlt />} onClick={exportToCSV} sx={{ height: '100%' }}>Export CSV</Button>
              </Grid>
            </Grid>
          </Paper>
        </LocalizationProvider>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Booking Id</TableCell>
                <TableCell>Table</TableCell>
                <TableCell>Date & Time</TableCell>
                <TableCell>Guests</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedBookings.map(booking => (
                <TableRow key={booking._id}>
                  <TableCell><Typography variant="body2">{booking.user.name}</Typography><Typography variant="caption" color="text.secondary">{booking.user.email}</Typography></TableCell>
                  <TableCell>{booking.bookingNumber}</TableCell>
                  <TableCell>Table {booking.table.tableNumber}</TableCell>
                  <TableCell>{new Date(booking.date).toLocaleDateString()} at {booking.time}</TableCell>
                  <TableCell>{booking.numberOfGuests}</TableCell>
                  <TableCell><Chip label={booking.status} color={booking.status === 'confirmed' ? 'success' : booking.status === 'pending' ? 'warning' : 'error'} size="small" /></TableCell>
                  <TableCell>
                    {['pending','cancelled','confirmed'].includes(booking.status) && (
                      <>
                        {booking.status !== 'confirmed' && <Button size="small" color="success" onClick={() => { setSelectedBookingId(booking._id); setNextStatus('confirmed'); setConfirmDialogOpen(true); }}>Confirm</Button>}
                        {booking.status !== 'cancelled' && <Button size="small" color="error" onClick={() => { setSelectedBookingId(booking._id); setNextStatus('cancelled'); setConfirmDialogOpen(true); }}>Cancel</Button>}
                      </>
                    )}
                    <Button size="small" onClick={() => { setEditingBooking(booking); setOpenBookingModal(true); }}>Edit</Button>
                  </TableCell>
                </TableRow>
              ))}
              {paginatedBookings.length === 0 && <TableRow><TableCell colSpan={7} align="center">No bookings found.</TableCell></TableRow>}
            </TableBody>
          </Table>
          <TablePagination component="div" count={filteredBookings.length} page={page} onPageChange={handleChangePage} rowsPerPage={rowsPerPage} onRowsPerPageChange={handleChangeRowsPerPage} rowsPerPageOptions={[5,10,25,50]} />
        </TableContainer>
      </Box>

      {/* Booking Modal */}
      <Dialog open={openBookingModal} onClose={() => { setOpenBookingModal(false); setEditingBooking(null); }} fullWidth maxWidth="lg">
        <BookTable prefilled={editingBooking} onSuccess={() => { fetchBookings(); setOpenBookingModal(false); setEditingBooking(null); }} />
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirm Action</DialogTitle>
        <DialogContent>Are you sure you want to <strong>{nextStatus}</strong> this booking?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateBookingStatus} variant="contained" color="primary">Yes, {nextStatus}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BookingsPage;
